// this will eventually be the main core component for editing Event-level interaction and setting their corresponding widgets
// the editor will allow for editing various  different things based on the supplied interaction type
// i.e. surveys will have a survey builder, for an alert its just text, for an email its just text
// for MVP this interface will be very simple - i.e. basic inputs. In future iterations this Editor will be MUCH more feature rich

// the interactions event editor is MUCH more comprehensive than its intervals counterpart

import React from "react";
import Interaction from "../../models/interactions/Interaction";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import CS1Survey from "./package/pages/editor/CS1Survey";
import {
  useStoreDispatch,
  useStoreSelector,
  useRowSelector,
} from "../../store/Hooks";
import useHttp from "../../hooks/useHttp";
import { interactionTreeSliceActions } from "../../store/slices/InteractionTreeSlice";
import LinearProgress from "@mui/material/LinearProgress";
import { useEffect, useState } from "react";
import EditPathway from "./pathway/pages/editor/EditPathway";
import EditorWrapper from "../ui/EditorWrapper";
import Account from "./account/pages/editor/Account";
import InteractionNode from "../../models/interactions/InteractionNode";
import { postgrest_operators } from "../../helpers/HttpHelper";
import InteractionInstanceProperty, { InteractionInstanceProperties } from "../../models/interactions/InteractionInstanceProperty";

const testing = process.env.REACT_APP_TEST_MODE === "test";

const INTERACTION_TYPES = [
  "cs1survey",
  "task",
  "alert",
  "information",
  "pathway",
  "account",
];

interface InteractionEditorProps {
  interaction: Interaction;
  interactionNode: InteractionNode | undefined;
  associatedIntervals: string | undefined;
  inDrawer?: boolean;
}

const InteractionEditor = (props: InteractionEditorProps) => {
  const dispatch = useStoreDispatch();
  const interaction = useRowSelector(Interaction, props.interaction.id);

  const INTERACTION_INSTANCE_PROPERTIES: InteractionInstanceProperties = {
    "anatomy": false
  };
  const [interactionInstanceProperties, setInteractionInstanceProperties] = useState<InteractionInstanceProperties>(INTERACTION_INSTANCE_PROPERTIES);

  const { sendRequest: fetchData } = useHttp();

  useEffect(() => {
    if (props.interaction) {
      fetchInteractionInstanceProperties(props.interaction.id)
    }
  }, [props.interaction]);

  // if (typeof interaction === "undefined") {
  //   return <LinearProgress />;
  // }

  const handleCloseWithoutSaving = async () => {
    // if we are closing without saving, we should revert redux UI for this interval
    // just pull the saved db state for this, and update the interval in redux with initial state
    Interaction.queryTable([
      {
        column: "id",
        operator: postgrest_operators.eq,
        value: props.interaction.id.toString(),
      },
    ]);

  };

  // updating interactions must save to redux state, since interactions are handled there
  const handleCS1SurveyMapping = (value: any) => {
    const cacheFile = value;
    // make sure to add type back in otherwise will break things
    const properties = {
      type: props.interaction.properties.type,
      cacheFile: cacheFile,
      instanceFields: ["anatomy"]
    };
    dispatch(
      interactionTreeSliceActions.updateInteraction({
        interactionID: props.interaction.id,
        properties: properties,
      }),
    );
  };

  // TODO - we dont really know exactly what these props will be yet?
  // updating interactions must save to redux state, since interactions are handled there
  const handlePathwayPropsMapping = (value: any) => {
    const properties = { prop: value };
    dispatch(
      interactionTreeSliceActions.updateInteraction({
        interactionID: props.interaction.id,
        properties: properties,
      }),
    );
  };

  // TODO - will need to pull in anchor properties, save them
  const handlePathwayAnchorMapping = (value: any) => {
    const properties = { prop: value };
    dispatch(
      interactionTreeSliceActions.updateInteraction({
        interactionID: props.interaction.id,
        properties: properties,
      }),
    );
  };

  // updating interactions must save to redux state, since interactions are handled there
  const handleNameMapping = (value: any) => {
    interaction?.update("title", value);
  };

  // updating interactions must save to redux state, since interactions are handled there
  const save = async () => {
    await interaction?.save();
    await InteractionInstanceProperty.saveAllInteractionInstanceProperties(interactionInstanceProperties, props.interaction.id)
  };

  const fetchInteractionInstanceProperties = async (
    interactionID: number,
  ) => {
    if (interactionID == -1 || !interactionID) {
      return;
    }
    const url: string =
      process.env.REACT_APP_API_URL + "/interaction_instance_property?interaction_id=eq." + interactionID;
    const requestSettings = {
      url: url,
      method: "GET",
      headers: {
        Authorization: "Bearer " + process.env.REACT_APP_POSTGREST_JWT,
        "Content-Type": "application/json",
        "Accept-Profile": testing ? "test" : "build"
      },
    };

    await fetchData(requestSettings, async (data: any) => {
      if (data && Array.isArray(data)) {
        const theseInteractionInstanceProperties: InteractionInstanceProperties = INTERACTION_INSTANCE_PROPERTIES;
        data.forEach((interactionInstanceProperty: InteractionInstanceProperty) => {
          theseInteractionInstanceProperties[interactionInstanceProperty.type as keyof InteractionInstanceProperties] = true;
        })
        setInteractionInstanceProperties(theseInteractionInstanceProperties)
      }
    });

  };

  //use type to figure out which event-editor template view to display
  let interactionContent: any;
  if (INTERACTION_TYPES.indexOf(props.interaction.properties.type) === -1) {
    interactionContent = <div>Interaction Type Not Properly Defined</div>;
  } else {
    switch (props.interaction.properties.type) {
      case "cs1survey": {
        // for MVP, focusing on CS1 surveys
        interactionContent = (
          <CS1Survey
            handleCS1SurveyMapping={handleCS1SurveyMapping}
            handleCS1NameMapping={handleNameMapping}
            setInteractionInstanceProperties={setInteractionInstanceProperties}
            interactionInstanceProperties={interactionInstanceProperties}
            defaultInteractionInstanceProperties={INTERACTION_INSTANCE_PROPERTIES}
            interaction={props.interaction}
            interactionNode={props.interactionNode}
            associatedIntervals={props.associatedIntervals}
          />
        );
        break;
      }
      case "task": {
        break;
      }
      case "alert": {
        break;
      }
      case "information": {
        break;
      }
      case "pathway": {
        interactionContent = (
          <EditPathway
            interaction={props.interaction}
            handlePathwayNameMapping={handleNameMapping}
            handlePathwayPropsMapping={handlePathwayPropsMapping}
            handlePathwayAnchorMapping={handlePathwayAnchorMapping}
          />
        );
        break;
      }
      case "account": {
        interactionContent = (
          <Account
            interaction={props.interaction}
            handleAccountNameMapping={handleNameMapping}
          />
        );
        break;
      }
      default: {
        interactionContent = <div>Interaction Type Not Properly Defined</div>;
        break;
      }
    }
  }

  const getSaveMessage = () => {
    return "Successfully saved Interaction " + interaction?.title;
  };

  const getDeleteMessage = () => {
    return "Successfully deleted Interaction " + interaction?.title;
  };

  if (props.interaction.properties.type === 'cs1survey' && props.interaction.id == -1) {
    return (
      <div style={{ width: "1300px" }}>
        {interactionContent}
      </div>
    )
  } else {
    return (
      <EditorWrapper
        save={save}
        cancel={handleCloseWithoutSaving}
        title={props.interaction.title}
        inDrawer={props.inDrawer}
        getSaveMessage={getSaveMessage}
        getDeleteMessage={getDeleteMessage}
      // accounts={true} // LEFT OFF HERE - THIS IS A GENERAL INTERACTIONS EDITOR SO ACCOUNTS ISNT ALWAYS = TRUE
      >
        <Box data-testid="interactionEditorTest">
          <Grid container style={{}}>
            <Grid item xs={12} style={{}}>
              {interactionContent}
            </Grid>
          </Grid>
        </Box>
      </EditorWrapper>
    );
  }


};

export default InteractionEditor;
